import { Fragment, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Download, CheckCircle2, Coins, Loader2, RotateCcw, MessageSquare, Unlock } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS = {
  awaiting_review: "bg-amber-500/15 text-amber-300",
  approved: "bg-emerald-500/15 text-emerald-300",
  resubmit_requested: "bg-orange-500/15 text-orange-300",
  training: "bg-neutral-800 text-neutral-400",
  deleted: "bg-red-500/10 text-red-400",
};
const STATUS_LABEL = {
  awaiting_review: "Awaiting Review", approved: "Approved", resubmit_requested: "Resubmission requested",
};

function ReviewPanel({ row, busy, onApprove, onResubmit, onClose }) {
  const [notes, setNotes] = useState("");
  return (
    <tr data-testid={`assessment-review-panel-${row.rm_user_id}`}>
      <td colSpan={6} className="bg-neutral-950 px-4 py-4">
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <p className="text-xs font-bold text-neutral-200">Review {row.rm_name}&rsquo;s Capital Readiness Report</p>
            <p className="mt-0.5 text-[11px] text-neutral-500">
              Notes are emailed to the RM. For a resubmission request, tell them exactly what to fix before they re-download and upload the report again.
            </p>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 2000))} rows={3}
              placeholder="e.g. Your report shows a score of 41 — please complete the Business Snapshot and upload management accounts, then re-download the report and resubmit."
              className="mt-2 w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-100 outline-none focus:border-[#0A8B85]"
              data-testid={`assessment-notes-${row.rm_user_id}`} />
            <p className="text-right text-[10px] text-neutral-600">{notes.length}/2000</p>
          </div>
          <div className="flex flex-col gap-2 md:w-60">
            <button onClick={() => onApprove(notes)} disabled={busy}
              className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white hover:bg-[#087a74] disabled:opacity-50"
              data-testid={`assessment-approve-${row.rm_user_id}`}>
              {busy === "approve" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve {notes.trim() ? "with notes" : ""}
            </button>
            <button onClick={() => onResubmit(notes)} disabled={busy || notes.trim().length < 5}
              title={notes.trim().length < 5 ? "Add a note explaining what the RM needs to do" : ""}
              className="inline-flex items-center justify-center gap-1 rounded-lg border border-orange-400/50 bg-orange-500/10 px-3 py-2 text-xs font-bold text-orange-200 hover:bg-orange-500/20 disabled:opacity-50"
              data-testid={`assessment-resubmit-${row.rm_user_id}`}>
              {busy === "resubmit" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Ask to re-download &amp; resubmit
            </button>
            <button onClick={onClose} className="text-[11px] font-semibold text-neutral-500 hover:text-neutral-300" data-testid={`assessment-review-cancel-${row.rm_user_id}`}>Cancel</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

export const RMAssessmentReview = () => {
  const [rows, setRows] = useState([]);
  const [awaiting, setAwaiting] = useState(0);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");
  const [credit, setCredit] = useState({ email: "", amount: "", platform: "clinq", note: "" });

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/admin/rm-assessments"), api.get("/admin/assessment-credits")]);
    setRows(a.data.submissions || []); setAwaiting(a.data.awaiting_count || 0); setLog(c.data.allocations || []);
    setUnlockLog(a.data.unlock_log || []);
  };
  useEffect(() => { load().catch(() => toast.error("Couldn't load assessments")); }, []);

  const [reviewing, setReviewing] = useState(null); // rm_user_id with the review panel open
  const [unlockLog, setUnlockLog] = useState([]);

  const unlockReport = async (r) => {
    const email = window.prompt(
      `Unlock the Capital Readiness Report on clinq.africa for ${r.rm_name}.\n\nWhich clinq.africa account did they use for Step 2?`,
      r.practical_email || r.rm_email);
    if (email === null) return;
    setBusy(`unlock-${r.submission_id}`);
    try {
      const { data } = await api.post(`/admin/rm-assessments/${r.submission_id}/unlock-report`, { email: email.trim() });
      toast.success(`Report unlocked for ${data.target_email}${data.rm_notified ? ` — ${r.rm_name} notified by email` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't unlock the report"); }
    setBusy("");
  };

  const approve = async (r, notes) => {
    if (!window.confirm(`Approve ${r.rm_name} as a Clinq RM and send the welcome email to ${r.rm_email}?`)) return;
    setBusy("approve");
    try {
      const { data } = await api.post(`/admin/rms/${r.rm_user_id}/approve`, { notes });
      toast.success(data.email_sent ? `${r.rm_name} approved — welcome email sent` : "Approved, but the email failed");
      setReviewing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't approve"); }
    setBusy("");
  };

  const requestResubmit = async (r, notes) => {
    setBusy("resubmit");
    try {
      const { data } = await api.post(`/admin/rms/${r.rm_user_id}/request-resubmission`, { notes });
      toast.success(data.email_sent ? `${r.rm_name} asked to re-download & resubmit — notes emailed` : "Saved, but the email failed — please tell the RM directly");
      setReviewing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the request"); }
    setBusy("");
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/admin/rm-assessments/${r.submission_id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = r.filename || "report.pdf"; a.click();
    } catch { toast.error("Couldn't download"); }
  };

  const allocate = async () => {
    setBusy("credit");
    try {
      const { data } = await api.post("/admin/assessment-credits", { ...credit, amount: Number(credit.amount) });
      if (data.allocation.status === "applied") toast.success(`${credit.amount} credit(s) added to ${credit.email}${data.allocation.platform === "prep" ? " on prep.clinq.africa" : ""}`);
      else toast.warning(`Queued for prep.clinq.africa — ${data.allocation.sync_error || "Prep not reachable"}. Use Retry once Prep is available.`);
      setCredit({ email: "", amount: "", platform: credit.platform, note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't allocate credits"); }
    setBusy("");
  };

  const retry = async (l) => {
    setBusy(l.allocation_id);
    try {
      const { data } = await api.post(`/admin/assessment-credits/${l.allocation_id}/retry`);
      if (data.allocation.status === "applied") toast.success(`Applied on prep.clinq.africa for ${l.email}`);
      else toast.warning(data.allocation.sync_error || "Still pending");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
    setBusy("");
  };

  const inp = "rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-100 outline-none focus:border-[#0A8B85]";

  return (
    <div className="space-y-8" data-testid="rm-assessment-review">
      <section>
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-neutral-200">Practical assessment submissions
            <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300" data-testid="assessment-awaiting-count">{awaiting} awaiting review</span>
          </p>
        </div>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-neutral-800">
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="assessment-table">
            <thead className="bg-neutral-900 text-[10px] uppercase tracking-wider text-neutral-400">
              <tr><th className="px-3 py-3">RM</th><th className="px-3 py-3">Email</th><th className="px-3 py-3">Submitted</th><th className="px-3 py-3">Report</th><th className="px-3 py-3">Status</th><th className="px-3 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-500" data-testid="assessment-empty">No practical assessments submitted yet.</td></tr>
              ) : rows.map((r) => (
                <Fragment key={r.submission_id}>
                <tr className={r.latest ? "" : "opacity-50"} data-testid={`assessment-row-${r.submission_id}`}>
                  <td className="px-3 py-2 font-semibold text-neutral-100">{r.rm_name}</td>
                  <td className="px-3 py-2 text-neutral-400">{r.rm_email}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-neutral-300">{fmt(r.submitted_at)}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => download(r)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#2dd4bf] hover:underline" data-testid={`assessment-download-${r.submission_id}`}>
                      <Download size={12} /> {r.filename}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS[r.rm_status] || STATUS.training}`} data-testid={`assessment-status-${r.submission_id}`}>
                      {STATUS_LABEL[r.rm_status] || r.rm_status}
                    </span>
                    {r.review_notes && (
                      <p className="mt-1 max-w-[260px] truncate text-[10px] text-neutral-500" title={r.review_notes} data-testid={`assessment-notes-preview-${r.submission_id}`}>
                        <MessageSquare size={10} className="mr-1 inline" />{r.review_notes}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.latest && r.rm_status === "awaiting_review" && (
                      <button onClick={() => setReviewing(reviewing === r.rm_user_id ? null : r.rm_user_id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#087a74]"
                        data-testid={`assessment-review-${r.rm_user_id}`}>
                        <CheckCircle2 size={12} /> Review
                      </button>
                    )}
                    {r.latest && r.rm_status === "resubmit_requested" && (
                      <span className="text-[11px] text-neutral-500" data-testid={`assessment-waiting-resubmit-${r.rm_user_id}`}>Waiting for RM to resubmit</span>
                    )}
                    {r.latest && (r.report_unlocked_at ? (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-300"
                        title={`Unlocked for ${r.report_unlocked_email} by ${r.report_unlocked_by}`}
                        data-testid={`assessment-report-unlocked-${r.submission_id}`}>
                        <Unlock size={10} /> Report unlocked · {fmt(r.report_unlocked_at)}
                      </span>
                    ) : (
                      <button onClick={() => unlockReport(r)} disabled={busy === `unlock-${r.submission_id}`}
                        className="mt-1 inline-flex items-center gap-1 rounded-lg border border-[#F59E0B]/50 px-3 py-1.5 text-xs font-bold text-[#F59E0B] hover:bg-[#F59E0B]/10 disabled:opacity-50"
                        data-testid={`assessment-unlock-report-${r.submission_id}`}>
                        <Unlock size={12} /> Unlock Capital Readiness Report
                      </button>
                    ))}
                  </td>
                </tr>
                {reviewing === r.rm_user_id && r.latest && (
                  <ReviewPanel row={r} busy={busy} onClose={() => setReviewing(null)}
                    onApprove={(notes) => approve(r, notes)} onResubmit={(notes) => requestResubmit(r, notes)} />
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {unlockLog.length > 0 && (
          <div className="mt-3 rounded-2xl border border-neutral-800 bg-neutral-950 p-4" data-testid="rm-unlock-log">
            <p className="text-xs font-bold text-neutral-300">Capital Readiness Report unlocks (clinq.africa)</p>
            <ul className="mt-2 divide-y divide-neutral-800 text-xs">
              {unlockLog.map((l) => (
                <li key={l.unlock_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5" data-testid={`rm-unlock-log-${l.unlock_id}`}>
                  <span className="font-semibold text-neutral-200">{l.rm_name}</span>
                  <span className="text-neutral-400">{l.rm_email}</span>
                  {l.target_email && l.target_email !== l.rm_email && <span className="text-neutral-500">→ {l.target_email}</span>}
                  <span className="text-neutral-500">{fmt(l.unlocked_at)}</span>
                  <span className="text-neutral-500">by {l.admin_email}</span>
                  {l.rm_notified && <span className="rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">RM emailed</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5" data-testid="credit-tool">
        <p className="flex items-center gap-2 text-sm font-bold text-neutral-200"><Coins size={15} className="text-[#2dd4bf]" /> Allocate Assessment Credits</p>
        <p className="mt-1 text-xs text-neutral-500">
          <b className="text-neutral-300">clinq.africa</b> credits are applied to the account instantly (readiness credits + detailed report unlocked).
          <b className="text-neutral-300"> prep.clinq.africa</b> credits are pushed straight to the Prep platform; if Prep can't be reached they stay "Pending sync" with a Retry button.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_120px_170px_1fr_auto]">
          <input type="email" placeholder="user@email.com" value={credit.email} onChange={(e) => setCredit({ ...credit, email: e.target.value })} className={inp} data-testid="credit-email" />
          <input type="number" min="1" max="500" placeholder="Credits" value={credit.amount} onChange={(e) => setCredit({ ...credit, amount: e.target.value })} className={inp} data-testid="credit-amount" />
          <select value={credit.platform} onChange={(e) => setCredit({ ...credit, platform: e.target.value })} className={inp} data-testid="credit-platform">
            <option value="clinq">clinq.africa (Step 2)</option>
            <option value="prep">prep.clinq.africa (Step 1)</option>
          </select>
          <input placeholder="Note (optional)" value={credit.note} onChange={(e) => setCredit({ ...credit, note: e.target.value })} className={inp} data-testid="credit-note" />
          <button onClick={allocate} disabled={busy === "credit" || !credit.email.includes("@") || !(Number(credit.amount) > 0)}
            className="rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#087a74] disabled:opacity-50" data-testid="credit-submit">
            {busy === "credit" ? "Allocating…" : "Allocate"}
          </button>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-neutral-800">
          <table className="w-full min-w-[700px] text-left text-xs" data-testid="credit-log">
            <thead className="bg-neutral-900 text-[10px] uppercase tracking-wider text-neutral-400">
              <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Platform</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">By</th><th className="px-3 py-2">Note</th></tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {log.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-4 text-center text-neutral-500">No credits allocated yet.</td></tr>
              ) : log.map((l) => (
                <tr key={l.allocation_id} data-testid={`credit-row-${l.allocation_id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-neutral-300">{fmt(l.allocated_at)}</td>
                  <td className="px-3 py-2 text-neutral-100">{l.email}</td>
                  <td className="px-3 py-2 font-bold text-[#2dd4bf]">{l.amount}</td>
                  <td className="px-3 py-2 text-neutral-300">{l.platform === "prep" ? "prep.clinq.africa" : "clinq.africa"}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${l.status === "applied" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} title={l.sync_error || ""}>{l.status === "applied" ? "Applied" : "Pending sync"}</span>
                    {l.status !== "applied" && l.platform === "prep" && (
                      <button onClick={() => retry(l)} disabled={busy === l.allocation_id} className="ml-2 text-[10px] font-bold text-[#2dd4bf] hover:underline disabled:opacity-50" data-testid={`credit-retry-${l.allocation_id}`}>Retry</button>
                    )}
                    {l.sync_error && l.status !== "applied" && <p className="mt-0.5 max-w-[220px] truncate text-[10px] text-neutral-500" title={l.sync_error}>{l.sync_error}</p>}
                  </td>
                  <td className="px-3 py-2 text-neutral-400">{l.allocated_by}</td>
                  <td className="px-3 py-2 text-neutral-500">{l.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
